import * as React from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { useForm } from '@tanstack/react-form'
import { create } from '@bufbuild/protobuf'
import { toast } from 'sonner'
import { captureError } from '@/lib/sentry'
import { reportError } from '@/lib/error-reporting'
import { AlertTriangle, Ban, Banknote, ArrowRight, Calendar, Hash } from 'lucide-react'

import type { ManualPayment } from '@/gen/cortado/v1/invoices_pb'
import { VoidManualPaymentRequestSchema } from '@/gen/cortado/v1/invoices_pb'
import { useVoidManualPayment, formatPaymentMethod } from '@/stores/payments.store'
import { formatCurrency } from '@/lib/currency-utils'
import { formatTimestamp } from '@/lib/date-utils'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

interface VoidPaymentDialogProps {
  invoiceId: string
  payment: ManualPayment | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function VoidPaymentDialog({
  invoiceId,
  payment,
  open,
  onOpenChange,
  onSuccess,
}: VoidPaymentDialogProps) {
  const { voidPayment, isVoiding } = useVoidManualPayment(invoiceId)

  const form = useForm({
    defaultValues: {
      reason: '',
      sendVoidNotification: false, // Default OFF - voids are often internal corrections
    },
    onSubmit: async ({ value }) => {
      try {
        if (!payment) {
          toast.error('No payment selected')
          return
        }

        if (!value.reason.trim()) {
          toast.error('Please provide a reason for voiding this payment')
          return
        }

        const request = create(VoidManualPaymentRequestSchema, {
          paymentId: payment.id,
          reason: value.reason.trim(),
          sendVoidNotification: value.sendVoidNotification,
        })

        await voidPayment(request)

        toast.success('Payment voided successfully')
        onOpenChange(false)
        form.reset()
        onSuccess?.()
      } catch (error) {
        console.error('Failed to void payment:', error)
        reportError(error, { feature: 'payment_void', kind: 'mutation' })
        captureError(error instanceof Error ? error : new Error(String(error)), { context: 'void-payment-dialog' })
        toast.error('Failed to void payment. Please try again.', {
          description: getErrorDescription(error),
        })
      }
    },
  })

  // Reset form when dialog opens
  React.useEffect(() => {
    if (open) {
      form.reset()
    }
  }, [open])

  if (!payment) return null

  const amountCents = Number(payment.amountCents)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-destructive" />
            Void Payment
          </DialogTitle>
          <DialogDescription>
            Mark this manual payment as void. The amount will be reversed on the invoice.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            form.handleSubmit()
          }}
          className="space-y-6"
        >
          {/* Payment Preview Card */}
          <div className="rounded-xl border-2 border-blue-100 dark:border-blue-900/50 bg-gradient-to-br from-blue-50/80 to-card dark:from-blue-950/30 dark:to-background p-4 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/40">
                <Banknote className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">{formatPaymentMethod(payment.paymentMethod)}</p>
                <p className="text-xs text-muted-foreground">
                  Manual Payment
                </p>
              </div>
              <div className="text-right">
                <p className="font-financial text-xl font-bold text-blue-600 dark:text-blue-400">
                  {formatCurrency(amountCents)}
                </p>
              </div>
            </div>

            {/* Payment details */}
            <div className="space-y-2 pt-2 border-t border-blue-100 dark:border-blue-800">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Payment Date</span>
                <span className="font-financial ml-auto font-medium">
                  {formatTimestamp(payment.paymentDate || payment.createdAt)}
                </span>
              </div>
              {payment.referenceNumber && (
                <div className="flex items-center gap-2 text-sm">
                  <Hash className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground">Reference</span>
                  <span className="font-financial ml-auto font-medium">
                    {payment.referenceNumber}
                  </span>
                </div>
              )}
              {payment.recordedByUser && (
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground ml-6">Recorded by</span>
                  <span className="ml-auto">
                    {payment.recordedByUser.firstName} {payment.recordedByUser.lastName}
                  </span>
                </div>
              )}
              {payment.notes && (
                <div className="text-sm text-muted-foreground italic mt-2 pt-2 border-t border-blue-100 dark:border-blue-800">
                  "{payment.notes}"
                </div>
              )}
            </div>
          </div>

          {/* Impact Preview */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Impact of voiding</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 text-center p-2 rounded bg-background">
                <p className="font-financial text-lg font-bold text-blue-600 dark:text-blue-400">
                  {formatCurrency(amountCents)}
                </p>
                <p className="text-xs text-muted-foreground">Current</p>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1 text-center p-2 rounded bg-background">
                <p className="font-financial text-lg font-bold text-destructive line-through">
                  {formatCurrency(amountCents)}
                </p>
                <p className="text-xs text-muted-foreground">Voided</p>
              </div>
            </div>
          </div>

          {/* Reason - Required */}
          <form.Field name="reason">
            {(field) => (
              <div className="space-y-2">
                <Label htmlFor="reason">
                  Reason for Voiding <span className="text-destructive">*</span>
                </Label>
                <Textarea
                  id="reason"
                  placeholder="Why is this payment being voided? (e.g., check bounced, duplicate entry, customer requested)"
                  rows={3}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  This reason is required and will be permanently recorded for audit purposes.
                </p>
              </div>
            )}
          </form.Field>

          {/* Send Void Notification Option */}
          <form.Field name="sendVoidNotification">
            {(field) => (
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="sendVoidNotification"
                  checked={field.state.value}
                  onCheckedChange={(checked) => field.handleChange(!!checked)}
                />
                <Label htmlFor="sendVoidNotification" className="text-sm font-normal text-muted-foreground cursor-pointer">
                  Notify customer about this void
                </Label>
              </div>
            )}
          </form.Field>

          {/* Warning */}
          <Alert variant="destructive" className="border-destructive/50 bg-destructive/5">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>This action cannot be undone</AlertTitle>
            <AlertDescription>
              Voiding will subtract {formatCurrency(amountCents)} from the invoice's paid amount.
            </AlertDescription>
          </Alert>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isVoiding}
            >
              Cancel
            </Button>
            <form.Subscribe selector={(s) => [s.values.reason, s.isSubmitting] as const}>
              {([reason, isSubmitting]) => (
                <Button
                  type="submit"
                  variant="destructive"
                  disabled={isVoiding || isSubmitting || !reason.trim()}
                >
                  {isVoiding ? 'Voiding...' : 'Void Payment'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
